import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  apiGet,
  apiPost,
  clearAuthToken,
  getAuthToken,
  setAuthToken
} from "@/services/api/client";

export interface AuthUser {
  id: string;
  username: string;
  role: AuthRole;
  displayName: string;
  isSuperAdmin: boolean;
  tenant: {
    id: string;
    name: string;
  };
}

export type AuthRole = "evaluator";

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface WireAuthUser extends Omit<AuthUser, "isSuperAdmin"> {
  isSuperAdmin?: boolean;
}

interface LoginResponse {
  token: string;
  user: WireAuthUser;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const token = getAuthToken();

    if (!token) {
      setIsAuthLoading(false);
      return () => {
        active = false;
      };
    }

    apiGet<WireAuthUser>("/me")
      .then((currentUser) => {
        if (active) {
          setUser(normalizeUser(currentUser));
        }
      })
      .catch(() => {
        clearAuthToken();
        if (active) {
          setUser(null);
        }
      })
      .finally(() => {
        if (active) {
          setIsAuthLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      clearAuthToken();
      setUser(null);
      queryClient.clear();
    };

    window.addEventListener("childplus:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("childplus:unauthorized", handleUnauthorized);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isAuthLoading,
      async login(username, password) {
        const response = await apiPost<LoginResponse>(
          "/auth/login",
          {
            username,
            password
          },
          { authenticated: false },
        );
        setAuthToken(response.token);
        queryClient.clear();
        setUser(normalizeUser(response.user));
      },
      async logout() {
        clearAuthToken();
        setUser(null);
        queryClient.clear();
      }
    }),
    [isAuthLoading, queryClient, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function normalizeUser(user: WireAuthUser): AuthUser {
  return { ...user, isSuperAdmin: user.isSuperAdmin === true };
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
